import React, { useState, useEffect, useMemo, useRef } from "react";
import { endOfDay, format, isSameDay, isValid, parse, startOfDay, subDays } from "date-fns";
import { useNavigate } from "react-router-dom";
import {  
  ChevronDown, 
  ListFilterPlus, 
  Plus, 
  Minus, 
  ShoppingCart,
  Gift,
  Wallet,
  ArrowUp,
  ArrowDown,
  CalendarCheck,
  Logs,
  CircleCheck
} from "lucide-react";
import TransactionHistoryFilter from "../../components/dashboard/TransactionHistoryFilter";
import "../../assets/css/TransactionHistory.css";

import { fetchTransactions } from "../../service/mywallet";


const TRANSACTION_PAGE_LIMIT = 10;
const createEmptyDateFilter = () => ({
  datePosted: "",
  dateRange: [null, null],
});

const TRANSACTION_FILTERS = [
  {
    id: "all",
    label: "All Transactions",
    apiValue: 0,
    icon: Logs,
    iconClassName: "",
    iconWrapperClassName: "",
  },
  {
    id: "earned",
    label: "Earned",
    apiValue: 1,
    icon: ArrowUp,
    iconClassName: "tw-text-[#039855]",
    iconWrapperClassName: "tw-bg-[#EDF9F3]",
  },
  {
    id: "spend",
    label: "Spend",
    apiValue: 2,
    icon: ArrowDown,
    iconClassName: "tw-text-[#E33B32]",
    iconWrapperClassName: "tw-bg-[#FFEFEE]",
  },
  {
    id: "withdraw",
    label: "Withdraw",
    apiValue: 3,
    icon: Wallet,
    iconClassName: "tw-text-[#323539]",
    iconWrapperClassName: "tw-bg-[#F1F1F1]",
  },
  {
    id: "daily-login-bonus",
    label: "Daily Login Bonus",
    apiValue: null,
    icon: CalendarCheck,
    iconClassName: "tw-text-[#EEA23E]",
    iconWrapperClassName: "tw-bg-[#FFFAF2]",
  },
  {
    id: "purchased",
    label: "Purchased",
    apiValue: 4,
    icon: Gift,
    iconClassName: "tw-text-[#0A77FF]",
    iconWrapperClassName: "tw-bg-[#ECF4FC]",
  },
];

const normalizeValue = (value) => String(value ?? "").trim().toLowerCase();

const includesAny = (value, keywords) =>
  keywords.some((keyword) => value.includes(keyword));

const getTransactionTypeCode = (transaction) => {
  const candidates = [
    transaction?.transaction_type,
    transaction?.transaction_type_id,
    transaction?.transaction_type_code,
    transaction?.type,
    transaction?.type_id,
  ];

  for (const candidate of candidates) {
    const parsed = Number(candidate);

    if (!Number.isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
};

const getTransactionCategory = (transaction) => {
  const transactionLabel = normalizeValue(transaction?.transaction_type_label);
  const transactionName = normalizeValue(transaction?.transaction_name);
  const transactionMode = normalizeValue(transaction?.transaction_mode);
  const typeCode = getTransactionTypeCode(transaction);

  if (
    includesAny(transactionName, ["daily login bonus", "login bonus"]) ||
    includesAny(transactionLabel, ["daily login bonus", "login bonus"])
  ) {
    return "daily-login-bonus";
  }

  if (includesAny(transactionLabel, ["earned"])) {
    return "earned";
  }

  if (includesAny(transactionLabel, ["purchased", "purchase"])) {
    return "purchased";
  }

  if (includesAny(transactionLabel, ["withdraw", "withdrawn"])) {
    return "withdraw";
  }

  if (includesAny(transactionLabel, ["redeemed", "redeem", "spent", "spend"])) {
    return "spend";
  }

  if (includesAny(transactionName, ["purchase", "purchased", "buy coins"])) {
    return "purchased";
  }

  if (includesAny(transactionName, ["withdraw", "withdrawal"])) {
    return "withdraw";
  }

  if (includesAny(transactionName, ["redeem", "redeemed", "spent", "spend"])) {
    return "spend";
  }

  if (transactionMode === "debit") {
    return "spend";
  }

  if (transactionMode === "credit") {
    return "earned";
  }

  switch (typeCode) {
    case 1:
      return "earned";
    case 2:
      return "spend";
    case 3:
      return "withdraw";
    case 4:
      return "purchased";
    default:
      return "all";
  }
};

const doesTransactionMatchFilter = (transaction, filterId) => {
  if (filterId === "all") {
    return true;
  }

  return getTransactionCategory(transaction) === filterId;
};

const getTransactionDisplayLabel = (transaction) => {
  const transactionCategory = getTransactionCategory(transaction);

  switch (transactionCategory) {
    case "earned":
      return "Earned";
    case "spend":
      return "Spend";
    case "withdraw":
      return "Withdraw";
    case "daily-login-bonus":
      return "Daily Login Bonus";
    case "purchased":
      return "Purchased";
    default:
      return transaction?.transaction_type_label || "Transaction";
  }
};

const isDebitTransaction = (transaction) =>
  normalizeValue(transaction?.transaction_mode) === "debit";

const parseTransactionDate = (value) => {
  if (!value) {
    return null;
  }

  const normalizedValue = String(value).trim();
  const supportedFormats = [
    "dd MMM yyyy, hh:mm a",
    "dd MMM yyyy hh:mm a",
    "dd MMM yyyy",
    "yyyy-MM-dd HH:mm:ss",
    "yyyy-MM-dd",
  ];

  for (const format of supportedFormats) {
    const parsedDate = parse(normalizedValue, format, new Date());

    if (isValid(parsedDate)) {
      return parsedDate;
    }
  }

  const fallbackDate = new Date(normalizedValue);
  return isValid(fallbackDate) ? fallbackDate : null;
};

const doesTransactionMatchDateFilter = (transaction, dateFilter) => {
  if (!dateFilter?.datePosted) {
    return true;
  }

  const transactionDate = parseTransactionDate(transaction?.created_on);

  if (!transactionDate) {
    return false;
  }

  const today = new Date();
  const todayEnd = endOfDay(today);

  switch (dateFilter.datePosted) {
    case "24 Hours":
    case "Today":
      return isSameDay(transactionDate, today);

    case "7 Days": {
      const lastSevenDaysStart = startOfDay(subDays(today, 6));
      return transactionDate >= lastSevenDaysStart && transactionDate <= todayEnd;
    }

    case "28 Days":
    case "30 Days": {
      const dayCount = dateFilter.datePosted === "28 Days" ? 27 : 29;
      const rangeStart = startOfDay(subDays(today, dayCount));
      return transactionDate >= rangeStart && transactionDate <= todayEnd;
    }

    case "Custom": {
      const [startDate, endDate] = dateFilter.dateRange ?? [];

      if (!startDate || !endDate) {
        return true;
      }

      return (
        transactionDate >= startOfDay(startDate) &&
        transactionDate <= endOfDay(endDate)
      );
    }

    default:
      return true;
  }
};

const TransactionHistory = () => {
  const navigate = useNavigate();

    const menuRef = useRef(null);
    const [activeAllTransactionsMenu, setActiveAllTransactionsMenu] = useState(null);
    const [transactionsFilterOpen, setTransactionsFilterOpen] = useState(null);

    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    const [selectedTransactionFilter, setSelectedTransactionFilter] = useState("all");
    const [dateFilter, setDateFilter] = useState(createEmptyDateFilter);

    

    // handle Click Outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
             setActiveAllTransactionsMenu(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);


  useEffect(() => {
    getTransactions(1);
  }, [selectedTransactionFilter, dateFilter]);

  
 const getTransactions = async (page = 1) => {
  try {
    setLoading(true);
    const token = localStorage.getItem("token");
    const selectedFilter = TRANSACTION_FILTERS.find((filter) => filter.id === selectedTransactionFilter);
    const params = {
      page,
      limit: TRANSACTION_PAGE_LIMIT,
      transaction_type: selectedFilter?.apiValue ?? undefined,
    };

    const dateFilterCodes = {
      "24 Hours": 1,
      "7 Days": 2,
      "28 Days": 3,
      Custom: 4,
    };
    const dateFilterCode = dateFilterCodes[dateFilter.datePosted];
    if (dateFilterCode) {
      params.date_filter = dateFilterCode;
    }
    if (dateFilterCode === 4 && dateFilter.dateRange?.[0] && dateFilter.dateRange?.[1]) {
      params.from_date = format(dateFilter.dateRange[0], "yyyy-MM-dd");
      params.to_date = format(dateFilter.dateRange[1], "yyyy-MM-dd");
    }

    const res = await fetchTransactions(token, params);
    const payload = res?.data?.data;

    if (res?.data?.status && payload) {
      setTransactions(payload.transactions ?? []);
      setTotalRecords(Number(payload.total_records ?? 0));
      setCurrentPage(Number(payload.current_page ?? page));
      setPerPage(Number(payload.per_page ?? TRANSACTION_PAGE_LIMIT));
    } else {
      setTransactions([]);
      setTotalRecords(0);
    }
  } catch (err) {
    console.log(err);
    setTransactions([]);
    setTotalRecords(0);
  } finally {
    setLoading(false);
  }
};


  const handleTransactionFilter = (filterId) => {
    setSelectedTransactionFilter(filterId);
    setActiveAllTransactionsMenu(false);
  };

  const handleDateFilterChange = ({ datePosted, dateRange = [null, null] }) => {
    setDateFilter({
      datePosted,
      dateRange,
    });
  };

  const handleTransactionFilterClose = () => {
    setTransactionsFilterOpen(false);
  };

  const activeTransactionFilter =
    TRANSACTION_FILTERS.find((filter) => filter.id === selectedTransactionFilter) ??
    TRANSACTION_FILTERS[0];

  const displayTransactions = transactions;

  const filteredTransactions = useMemo(
    () =>
      displayTransactions.filter((transaction) =>
        doesTransactionMatchFilter(transaction, selectedTransactionFilter) &&
        doesTransactionMatchDateFilter(transaction, dateFilter)
      ),
    [displayTransactions, selectedTransactionFilter, dateFilter]
  );

  const visibleTotalRecords = activeTransactionFilter.apiValue === null
    ? filteredTransactions.length
    : totalRecords;
  const totalPages = visibleTotalRecords > 0 ? Math.ceil(visibleTotalRecords / perPage) : 0;
  const firstVisibleRecord = visibleTotalRecords === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const lastVisibleRecord = visibleTotalRecords === 0
    ? 0
    : Math.min(currentPage * perPage, visibleTotalRecords);
  const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1);


  return (
    <>
    <div className="card border-0 shadow-sm rounded-4 p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold ft-18 mb-1">Transaction History</h2>
          <p className="ft-12 mb-0 tw-text-[#525D6A]">
            Track All Your Wallet Transactions And Account Activities.
          </p>
        </div>

        <div className="d-flex gap-3 position-relative">
          <div
            className="ft-14 graytext px-3 py-3 rounded-2 border border-1 tw-cursor-pointer d-flex gap-2 align-items-center position-relative"
            ref={menuRef}
            onClick={() =>
              setActiveAllTransactionsMenu((previousState) => !previousState)
            }
          >
            {activeTransactionFilter.label} <ChevronDown size={20} />

            {activeAllTransactionsMenu && (
                <div
                  className="cs-question-menu tw-w-[210px]"
                  onClick={(event) => event.stopPropagation()}
                >
                    <div className="d-flex gap-1 flex-column w-100">

                      {TRANSACTION_FILTERS.map((filter) => {
                        const Icon = filter.icon;
                        const isSelected =
                          selectedTransactionFilter === filter.id;

                        return (
                          <button
                            key={filter.id}
                            type="button"
                            className={`cs-action-link ${
                              filter.id === "all"
                                ? "d-flex align-items-center justify-content-between !tw-bg-[#F6F6F6]"
                                : ""
                            }`}
                            onClick={() => handleTransactionFilter(filter.id)}
                          >
                            {filter.id === "all" ? (
                              <Icon size={16} />
                            ) : (
                              <div
                                className={`rounded-circle d-flex align-items-center justify-content-center tw-w-[24px] tw-h-[24px] ${filter.iconWrapperClassName}`}
                              >
                                <Icon size={14} className={filter.iconClassName} />
                              </div>
                            )}

                            <span>{filter.label}</span>

                            {isSelected && (
                              <CircleCheck
                                size={16}
                                className="tw-text-[#20895F] ms-auto"
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                </div>
            )}

          </div>

          <button
            type="button"
            className={`transaction-filter-trigger ft-14 graytext px-4 py-3 rounded-2 border border-1 tw-cursor-pointer d-flex gap-2 align-items-center ${transactionsFilterOpen ? "is-open" : ""}`}
            aria-label="Show transaction filters"
            aria-expanded={Boolean(transactionsFilterOpen)}
            aria-controls="transaction-filter-drawer"
            data-tooltip="Show transaction filters"
            onClick={() =>
              setTransactionsFilterOpen((previousState) => !previousState)
            }
          >
            <ListFilterPlus size={18} /> Filter
            {dateFilter.datePosted && <span className="transaction-filter-count">1</span>}
          </button>           


        </div>
      </div>

      {/* Table */}
      <table className="table align-middle">
        <thead className="table-light ft-12 font-semibold tw-text-[#323539]">
          <tr>
            <th>Transaction</th>
            <th>Type</th>
            <th>Coins</th>
            <th>Date & Time</th>
          </tr>
        </thead>

        <tbody>
          {loading && (
            <tr>
              <td colSpan={4} className="text-center text-muted ft-13 py-4">
                Loading transactions...
              </td>
            </tr>
          )}

          {!loading && filteredTransactions.length === 0 && (
            <tr>
              <td colSpan={4} className="text-center text-muted ft-13 py-4">
                No {activeTransactionFilter.label.toLowerCase()} found.
              </td>
            </tr>
          )}

          {!loading && filteredTransactions.map((item) => {
            const transactionCategory = getTransactionCategory(item);
            const transactionDisplayLabel = getTransactionDisplayLabel(item);
            let bgColor = "";
            let textColor = "";
            let Icon = Plus;

            switch (transactionCategory) {
              case "earned":
                bgColor = "tw-bg-[#EDF9F3]";
                textColor = "tw-text-[#20895F]";
                Icon = Plus;
                break;

              case "spend":
                bgColor = "tw-bg-[#FDECEC]";
                textColor = "tw-text-[#EF4444]";
                Icon = Minus;
                break;

              case "purchased":
                bgColor = "tw-bg-[#FFF8E6]";
                textColor = "tw-text-[#F59E0B]";
                Icon = ShoppingCart;
                break;

              case "withdraw":
                bgColor = "tw-bg-[#F3F4F6]";
                textColor = "tw-text-[#6B7280]";
                Icon = Wallet;
                break;

              default:
                bgColor = "tw-bg-[#F3F4F6]";
                textColor = "tw-text-[#6B7280]";
            }

            return (
              <tr key={item.transaction_id}>
                <td className="py-3">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className={`rounded-circle d-flex align-items-center justify-content-center tw-w-[24px] tw-h-[24px] ${bgColor}`}
                    >
                      <Icon size={16} className={textColor} />
                    </div>

                    <div>
                      <h6 className="fw-bold ft-13 mb-1">
                        {item.transaction_name}
                      </h6>
                      {item.transaction_description && (
                        <p className="ft-11 mb-0 tw-text-[#6B7280]">
                          {item.transaction_description}
                        </p>
                      )}
                    </div>
                  </div>
                </td>

                <td className="py-3">
                  <span
                    className={`badge rounded-pill px-4 py-2 ft-12 fw-semibold ${bgColor} ${textColor}`}
                  >
                    {transactionDisplayLabel}
                  </span>
                </td>

                <td className={`fw-bold ft-12 py-3 ${textColor}`}>
                  {isDebitTransaction(item)
                    ? `-${item.coins}`
                    : `+${item.coins}`}
                </td>

                <td className="text-muted ft-12 py-3">
                  {item.created_on}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mt-4">
        <p className="ft-12 color858c95 mb-0">
          Showing {firstVisibleRecord} to {lastVisibleRecord} of {visibleTotalRecords} Transactions
        </p>

        <nav aria-label="Transaction pages">
          <ul className="pagination mb-0 gap-2">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button className="page-link" type="button" disabled={currentPage === 1} onClick={() => getTransactions(currentPage - 1)}>
                &lt;-
              </button>
            </li>
            {pageNumbers.map((page) => (
              <li className={`page-item ${page === currentPage ? "active" : ""}`} key={page}>
                <button className="page-link" type="button" onClick={() => getTransactions(page)}>{page}</button>
              </li>
            ))}
            <li className={`page-item ${currentPage >= totalPages ? "disabled" : ""}`}>
              <button className="page-link" type="button" disabled={currentPage >= totalPages} onClick={() => getTransactions(currentPage + 1)}>
                -&gt;
              </button>
            </li>
          </ul>
        </nav>
      </div>

      <div className="wallet-help-strip d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mt-4">
        <div>
          <h6 className="ft-13 fw-bold mb-1">Need help?</h6>
          <p className="ft-12 mb-0">If you have any questions about transactions, please contact our support team.</p>
        </div>
        <button
          type="button"
          className="redhoverbtn bgwhite border rounded-2 px-4 py-2 ft-12 fw-bold"
          onClick={() => navigate("/settings/support")}
        >
          Contact Support
        </button>
      </div>
    </div>



       <TransactionHistoryFilter
         isOpen={Boolean(transactionsFilterOpen)}
         dateFilter={dateFilter}
         onDateFilterChange={handleDateFilterChange}
         onClose={handleTransactionFilterClose}
       />
    </>




  );
};

export default TransactionHistory;
